import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Block } from '@sorare/core/src/atoms/layout/Block';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ButtonWithConfirmDialog } from '@sorare/core/src/components/form/ButtonWithConfirmDialog';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { breakpoints, tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { AuctionReminder } from '#src/components/auction/AuctionReminder/index.tsx';
import { BidInfos } from '#src/components/auction/BidInfos/index.tsx';
import { BidField } from '#src/components/buyActions/BidField/index.tsx';
import { PromoBanners } from '#src/components/market/PromoBanners/index.tsx';
import { useBestBidBelongsToUser } from '#src/hooks/auctions/useBestBidBelongsToUser.ts';
import { useStopAutoBid } from '#src/hooks/auctions/useStopAutoBid.ts';

import type { OpenAuction_auction } from './__generated__/index.graphql.ts';

const Root = styled(Block)`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);

  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }
`;
const ButtonsContainer = styled.div`
  display: flex;
  gap: var(--unit);

  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    justify-content: space-between;
  }
`;
const StopButton = styled(ButtonWithConfirmDialog)`
  @media (max-width: ${breakpoints.tablet}px) {
    order: 1;
  }
`;

const messages = defineMessages({
  stopAutoBidConfirm: {
    defaultMessage:
      'Are you sure you want to stop bidding on the auction? Your current bid of {amount} will remain but you will not auto-bid further.',
  },
});

type Props = {
  auction: OpenAuction_auction;
  showBundleContext?: boolean;
};

const Price = ({ monetaryAmount }: { monetaryAmount: MonetaryAmount }) => {
  const { main } = useAmountWithConversion({
    monetaryAmount,
  });

  return <b>{main}</b>;
};

const ConfirmMessage = ({ amounts }: { amounts: MonetaryAmount }) => {
  if (amounts.isZero) {
    return null;
  }
  return (
    <div>
      <FormattedMessage
        {...messages.stopAutoBidConfirm}
        values={{
          amount: <Price monetaryAmount={amounts} />,
        }}
      />
    </div>
  );
};

export const OpenAuction = ({ auction, showBundleContext }: Props) => {
  const isTablet = useIsTabletAndAbove();
  const bestBidBelongsToUser = useBestBidBelongsToUser();
  const stopAutoBid = useStopAutoBid(auction.bestBid!);
  const { toMonetaryAmount } = useMonetaryAmount();

  if (!auction) return null;

  const { bestBid, myBestBid } = auction;

  const bestBidAmount = toMonetaryAmount(bestBid?.amounts);
  const myBestBidAmount = toMonetaryAmount(myBestBid?.maximumAmounts);

  const willAutoBid =
    bestBid &&
    myBestBid &&
    bestBidBelongsToUser(bestBid) &&
    myBestBidAmount.gt(bestBidAmount);

  return (
    <Root>
      <Vertical gap={0.5}>
        <BidInfos
          auction={auction}
          withReminder={!isTablet}
          showBundleContext={showBundleContext}
        />
      </Vertical>
      <ButtonsContainer>
        {!!willAutoBid && (
          <StopButton
            color="red"
            size="medium"
            message={<ConfirmMessage amounts={bestBidAmount} />}
            onConfirm={() => {
              stopAutoBid();
            }}
          >
            <FormattedMessage defaultMessage="Stop" />
          </StopButton>
        )}
        <BidField variant="medium" auction={auction} />
        {!!isTablet && <AuctionReminder color="tertiary" auction={auction} />}
      </ButtonsContainer>
      <PromoBanners
        promotionalEvents={auction}
        wheelTicketRewards={auction}
        rounded
      />
    </Root>
  );
};

OpenAuction.fragments = {
  auction: gql`
    fragment OpenAuction_auction on TokenAuction {
      id
      cancelled
      bestBid {
        id
        amounts {
          ...useMonetaryAmount_monetaryAmount
        }
        ...useStopAutoBid_bid
        ...UseBestBidBelongsToUser_bestBid
      }
      myBestBid {
        id
        maximumAmounts {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      ...BidField_auction
      ...BidInfos_auction
      ...AuctionReminder_auction
      ...PromoBanners_PromotionalEventsInterface
      ...PromoBanners_WheelTicketRewardsInterface
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${BidField.fragments.auction}
    ${BidInfos.fragments.auction}
    ${useStopAutoBid.fragments.bid}
    ${useBestBidBelongsToUser.fragments.bestBid}
    ${AuctionReminder.fragments.auction}
    ${PromoBanners.fragments.PromotionalEventsInterface}
    ${PromoBanners.fragments.WheelTicketRewardsInterface}
  ` as TypedDocumentNode<OpenAuction_auction>,
};
